/**
 * floorScene — vanilla Three.js engine for the Digital Twin 3D viewer.
 *
 * Ported from the Claude Design handoff (`scene.js`). Differences:
 *   - ES module imports (no UMD globals)
 *   - three@0.160+ color-space API (`outputColorSpace`, `SRGBColorSpace`)
 *   - Units fed in via opts instead of a window global so the scene can be
 *     rebuilt on payload/floor change.
 *
 * Exposes `createFloorScene(container, opts) → api`:
 *   api.setTheme(name)          // 'cyber' | 'teal' | 'ember'
 *   api.setHeatmap(mode)        // 'off' | 'footfall' | 'ocr' | 'anomaly'
 *   api.setShowGrid(bool)
 *   api.setShowLabels(bool)
 *   api.setGlass(0..1)
 *   api.setUnits(units, planArea, floorPlanUrl)   // re-seed scene
 *   api.focusUnit(id)
 *   api.resetView()
 *   api.dispose()
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { THEMES, HEAT_RAMP } from './themes';

const DEFAULT_FRUSTUM = 22;

function lerpColor(a, b, t) {
    return new THREE.Color(a).lerp(new THREE.Color(b), t);
}
function rampColor(t) {
    t = Math.max(0, Math.min(1, t));
    const seg = t * (HEAT_RAMP.length - 1);
    const i = Math.floor(seg);
    if (i >= HEAT_RAMP.length - 1) return new THREE.Color(HEAT_RAMP[HEAT_RAMP.length - 1]);
    return lerpColor(HEAT_RAMP[i], HEAT_RAMP[i + 1], seg - i);
}
function roundedRectShape(w, d, r) {
    const x = -w / 2, y = -d / 2, s = new THREE.Shape();
    r = Math.min(r, w / 2, d / 2);
    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + d - r); s.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
    s.lineTo(x + r, y + d); s.quadraticCurveTo(x, y + d, x, y + d - r);
    s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
    return s;
}
function heatValue(d, mode) {
    if (mode === 'footfall') return Number(d.kpis?.footfall_share) || 0;
    if (mode === 'ocr') return Number(d.kpis?.ocr_pct) || 0;
    if (mode === 'anomaly') return Number(d.kpis?.anomaly_count) || 0;
    return 0;
}

export function createFloorScene(container, opts = {}) {
    const onHover = opts.onHover || (() => {});
    const onSelect = opts.onSelect || (() => {});

    let theme = THEMES[opts.theme || 'cyber'];
    let heatMode = 'off';
    let showGrid = true;
    let showLabels = true;
    let glass = 0.78;
    let selectedId = null;
    let hoveredId = null;

    let unitsData = opts.units || [];
    let planArea = opts.planArea || { w: 28, d: 20 };
    let floorPlanUrl = opts.floorPlanUrl || null;

    // ─── renderer ────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({
        antialias: true, alpha: false, powerPreference: 'high-performance', preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = theme.scene.exposure;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);
    renderer.domElement.style.display = 'block';

    // ─── scene + camera ──────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(theme.scene.bg);
    scene.fog = new THREE.Fog(theme.scene.fog, 34, 64);

    let aspect = 1;
    const FRUSTUM = DEFAULT_FRUSTUM;
    const camera = new THREE.OrthographicCamera(-FRUSTUM, FRUSTUM, FRUSTUM, -FRUSTUM, 0.1, 200);
    camera.position.set(20, 22, 20);
    camera.zoom = 1;
    camera.lookAt(0, 0, 0);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minZoom = 0.6;
    controls.maxZoom = 3.2;
    controls.maxPolarAngle = Math.PI * 0.46;
    controls.minPolarAngle = Math.PI * 0.12;
    controls.target.set(0, 0.6, 0);

    // ─── lights ──────────────────────────────────────────────────
    const hemi = new THREE.HemisphereLight(0xffffff, 0x0a0a12, 0.35); scene.add(hemi);
    const ambient = new THREE.AmbientLight(0xffffff, 0.22); scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 1.35);
    key.position.set(14, 24, 12); key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -26; key.shadow.camera.right = 26;
    key.shadow.camera.top = 26; key.shadow.camera.bottom = -26;
    key.shadow.camera.near = 1; key.shadow.camera.far = 80;
    key.shadow.bias = -0.0004; key.shadow.radius = 6;
    scene.add(key);
    const rim = new THREE.PointLight(0xffffff, 0.9, 80, 2); rim.position.set(-16, 10, -14); scene.add(rim);
    const fill = new THREE.PointLight(0xffffff, 0.6, 80, 2); fill.position.set(16, 8, 14); scene.add(fill);

    // ─── floor + grid ────────────────────────────────────────────
    const floorMat = new THREE.MeshStandardMaterial({
        color: theme.scene.floor, roughness: 0.92, metalness: 0.1,
        emissive: theme.scene.floorEmissive, emissiveIntensity: 0.45,
    });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(56, 40), floorMat);
    floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true;
    scene.add(floor);

    // optional faint uploaded plan under the grid
    let planMesh = null, planMat = null;
    function rebuildPlanMesh() {
        if (planMesh) { scene.remove(planMesh); planMesh.geometry.dispose(); planMat.dispose(); planMesh = null; planMat = null; }
        if (!floorPlanUrl) return;
        planMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.26, depthWrite: false, color: 0xffffff });
        planMesh = new THREE.Mesh(new THREE.PlaneGeometry(planArea.w, planArea.d), planMat);
        planMesh.rotation.x = -Math.PI / 2;
        planMesh.position.y = 0.006;
        scene.add(planMesh);
        new THREE.TextureLoader().load(floorPlanUrl, (tex) => {
            tex.colorSpace = THREE.SRGBColorSpace;
            planMat.map = tex; planMat.needsUpdate = true;
        });
    }

    const gridGroup = new THREE.Group(); scene.add(gridGroup);
    const gridMajor = new THREE.GridHelper(56, 28, theme.scene.gridMajor, theme.scene.gridMajor);
    gridMajor.material.transparent = true; gridMajor.material.opacity = 0.55; gridMajor.position.y = 0.012;
    const gridMinor = new THREE.GridHelper(56, 112, theme.scene.gridMinor, theme.scene.gridMinor);
    gridMinor.material.transparent = true; gridMinor.material.opacity = 0.18; gridMinor.position.y = 0.008;
    gridGroup.add(gridMinor, gridMajor);

    const frame = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(planArea.w, 0.01, planArea.d)),
        new THREE.LineBasicMaterial({ color: theme.scene.gridMajor, transparent: true, opacity: 0.6 }),
    );
    frame.position.y = 0.02; scene.add(frame);

    // ─── units ───────────────────────────────────────────────────
    const unitGroup = new THREE.Group(); scene.add(unitGroup);
    let units = [];      // { data, mesh, edges, mat, plate, topY }
    const labelEls = {};

    const overlay = document.createElement('div');
    overlay.className = 'twin3d-overlay';
    container.appendChild(overlay);

    function disposeUnits() {
        units.forEach((u) => {
            unitGroup.remove(u.mesh, u.edges, u.plate);
            u.mesh.geometry.dispose(); u.mat.dispose();
            u.edges.geometry.dispose(); u.edges.material.dispose();
            u.plate.geometry.dispose(); u.plate.material.dispose();
        });
        units = [];
        Object.values(labelEls).forEach((el) => el.remove());
        for (const k of Object.keys(labelEls)) delete labelEls[k];
    }

    function makeUnit(d) {
        const g = d.geom;
        const shape = roundedRectShape(g.w, g.d, 0.55);
        const geo = new THREE.ExtrudeGeometry(shape, {
            depth: g.h, bevelEnabled: true, bevelThickness: 0.12, bevelSize: 0.12, bevelSegments: 3, steps: 1,
        });
        geo.rotateX(-Math.PI / 2);
        geo.translate(0, g.h, 0);
        const mat = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(theme.scene.glassTint),
            metalness: 0, roughness: 0.16,
            transmission: glass, thickness: 2.4, ior: 1.32,
            clearcoat: 0.7, clearcoatRoughness: 0.28,
            attenuationColor: new THREE.Color(theme.scene.edge), attenuationDistance: 1.6,
            emissive: new THREE.Color(theme.scene.edge), emissiveIntensity: 0.0,
            envMapIntensity: 1.0, transparent: false,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.castShadow = true; mesh.receiveShadow = true;
        mesh.position.set(g.x, 0, g.z);
        mesh.userData.id = d.id;

        const eGeo = new THREE.EdgesGeometry(geo, 25);
        const edges = new THREE.LineSegments(eGeo, new THREE.LineBasicMaterial({
            color: new THREE.Color(theme.scene.edge), transparent: true, opacity: 0.85,
        }));
        edges.position.copy(mesh.position);

        const plateShape = roundedRectShape(g.w + 0.5, g.d + 0.5, 0.7);
        const plateGeo = new THREE.ShapeGeometry(plateShape);
        plateGeo.rotateX(-Math.PI / 2);
        const plate = new THREE.Mesh(plateGeo, new THREE.MeshBasicMaterial({
            color: new THREE.Color(theme.scene.edge), transparent: true, opacity: 0.06,
        }));
        plate.position.set(g.x, 0.015, g.z);

        unitGroup.add(mesh, edges, plate);

        const el = document.createElement('div');
        el.className = 'twin3d-label';
        el.innerHTML = '<span class="tl-name"></span><span class="tl-sub"></span>';
        el.querySelector('.tl-name').textContent = d.storeName;
        el.querySelector('.tl-sub').textContent = d.unitCode;
        overlay.appendChild(el);
        labelEls[d.id] = el;

        units.push({ data: d, mesh, edges, mat, plate, topY: g.h + 0.2 });
    }

    function rebuildUnits() {
        disposeUnits();
        unitsData.forEach(makeUnit);
        applyVisuals();
    }

    // ─── raycasting ──────────────────────────────────────────────
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    function setPointerFromEvent(e) {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    }
    function pickUnit() {
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects(units.map((u) => u.mesh), false);
        return hits.length ? hits[0].object.userData.id : null;
    }
    const onPointerMove = (e) => {
        setPointerFromEvent(e);
        const id = pickUnit();
        if (id !== hoveredId) {
            hoveredId = id;
            renderer.domElement.style.cursor = id ? 'pointer' : 'grab';
            const u = units.find((x) => x.data.id === id);
            onHover(u ? u.data : null);
            applyVisuals();
        }
    };
    const onPointerLeave = () => { hoveredId = null; onHover(null); applyVisuals(); };
    const onPointerDown = (e) => { downPos = { x: e.clientX, y: e.clientY }; };
    const onClick = (e) => {
        if (downPos && (Math.abs(e.clientX - downPos.x) > 5 || Math.abs(e.clientY - downPos.y) > 5)) return;
        setPointerFromEvent(e);
        const id = pickUnit();
        if (id) selectUnit(id);
    };
    let downPos = null;
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('click', onClick);

    // ─── selection + camera focus ────────────────────────────────
    const camGoal = { target: controls.target.clone(), zoom: 1 };
    const homeTarget = new THREE.Vector3(0, 0.6, 0);
    let focusAnimating = false;

    function selectUnit(id) {
        selectedId = id;
        const u = units.find((x) => x.data.id === id);
        if (!u) return;
        camGoal.target.set(u.data.geom.x, u.data.geom.h * 0.5, u.data.geom.z);
        camGoal.zoom = 1.7;
        focusAnimating = true;
        onSelect(u.data);
        applyVisuals();
    }
    function deselect() {
        selectedId = null;
        camGoal.target.copy(homeTarget);
        camGoal.zoom = 1;
        focusAnimating = true;
        onSelect(null);
        applyVisuals();
    }

    // ─── visuals (theme / heat / selection / hover) ──────────────
    function heatRange() {
        const vals = unitsData.map((d) => heatValue(d, heatMode));
        return { min: Math.min(...vals), max: Math.max(...vals) };
    }
    function applyVisuals() {
        const heating = heatMode !== 'off' && unitsData.length > 0;
        const rng = heating ? heatRange() : null;
        units.forEach((u) => {
            const d = u.data;
            const isSel = d.id === selectedId;
            const isHov = d.id === hoveredId;
            let tint, edgeCol, emiss;
            if (heating) {
                const t = rng.max === rng.min ? 0.5 : (heatValue(d, heatMode) - rng.min) / (rng.max - rng.min);
                const c = rampColor(t);
                tint = c.clone().lerp(new THREE.Color('#ffffff'), 0.18);
                edgeCol = c.clone();
                emiss = c.clone();
                u.mat.emissiveIntensity = 0.5 + (isSel ? 0.7 : isHov ? 0.35 : 0.0);
            } else {
                tint = new THREE.Color(theme.scene.glassTint);
                edgeCol = new THREE.Color(theme.scene.edge);
                emiss = new THREE.Color(theme.scene.edge);
                u.mat.emissiveIntensity = isSel ? 0.55 : isHov ? 0.28 : 0.0;
            }
            u.mat.color.copy(tint);
            u.mat.emissive.copy(emiss);
            u.mat.transmission = heating ? Math.min(glass, 0.62) : glass;
            u.mat.attenuationColor.copy(edgeCol);
            u.edges.material.color.copy(edgeCol);
            u.edges.material.opacity = isSel ? 1.0 : isHov ? 0.95 : 0.8;
            u.plate.material.color.copy(edgeCol);
            u.plate.material.opacity = isSel ? 0.22 : isHov ? 0.13 : 0.06;
        });
    }

    function applyTheme() {
        const s = theme.scene;
        scene.background.set(s.bg); scene.fog.color.set(s.fog);
        renderer.toneMappingExposure = s.exposure;
        floorMat.color.set(s.floor); floorMat.emissive.set(s.floorEmissive);
        gridMajor.material.color.set(s.gridMajor);
        gridMinor.material.color.set(s.gridMinor);
        frame.material.color.set(s.gridMajor);
        bloomPass.strength = s.bloom;
        applyVisuals();
    }

    // ─── post-processing (bloom) ─────────────────────────────────
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), theme.scene.bloom, 0.6, 0.82);
    composer.addPass(bloomPass);

    // ─── resize ──────────────────────────────────────────────────
    function resize() {
        const w = container.clientWidth, h = container.clientHeight;
        if (!w || !h) return;
        aspect = w / h;
        camera.left = -FRUSTUM * aspect; camera.right = FRUSTUM * aspect;
        camera.top = FRUSTUM; camera.bottom = -FRUSTUM;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
        composer.setSize(w, h);
        bloomPass.setSize(w, h);
    }
    const ro = new ResizeObserver(resize); ro.observe(container);
    resize();

    // ─── label projection ────────────────────────────────────────
    const tmp = new THREE.Vector3();
    function updateLabels() {
        const w = container.clientWidth, h = container.clientHeight;
        units.forEach((u) => {
            const el = labelEls[u.data.id];
            if (!el) return;
            if (!showLabels || (selectedId && selectedId !== u.data.id)) { el.style.display = 'none'; return; }
            tmp.set(u.data.geom.x, u.topY + 0.4, u.data.geom.z).project(camera);
            if (tmp.z > 1) { el.style.display = 'none'; return; }
            el.style.display = 'flex';
            el.style.left = ((tmp.x * 0.5 + 0.5) * w) + 'px';
            el.style.top = ((-tmp.y * 0.5 + 0.5) * h) + 'px';
            el.classList.toggle('is-active', u.data.id === selectedId || u.data.id === hoveredId);
        });
    }

    // ─── animation loop ──────────────────────────────────────────
    let raf = 0, t0 = performance.now();
    function tick(now) {
        raf = requestAnimationFrame(tick);
        const dt = Math.min((now - t0) / 1000, 0.05); t0 = now;
        if (focusAnimating) {
            const k = 1 - Math.pow(0.0009, dt);
            controls.target.lerp(camGoal.target, k);
            camera.zoom += (camGoal.zoom - camera.zoom) * k;
            camera.updateProjectionMatrix();
            if (controls.target.distanceTo(camGoal.target) < 0.04 && Math.abs(camera.zoom - camGoal.zoom) < 0.01) {
                controls.target.copy(camGoal.target); camera.zoom = camGoal.zoom; focusAnimating = false;
            }
        }
        controls.update();
        if (selectedId) {
            const u = units.find((x) => x.data.id === selectedId);
            if (u) u.edges.material.opacity = 0.7 + 0.3 * (0.5 + 0.5 * Math.sin(now / 320));
        }
        composer.render();
        updateLabels();
    }

    rebuildPlanMesh();
    rebuildUnits();
    applyTheme();
    raf = requestAnimationFrame(tick);

    // ─── public api ──────────────────────────────────────────────
    return {
        setTheme(name) { if (THEMES[name]) { theme = THEMES[name]; applyTheme(); } },
        setHeatmap(mode) { heatMode = mode || 'off'; applyVisuals(); },
        setShowGrid(v) { showGrid = !!v; gridGroup.visible = showGrid; frame.visible = showGrid; },
        setShowLabels(v) { showLabels = !!v; updateLabels(); },
        setGlass(v) { glass = Number(v); applyVisuals(); },
        setUnits(nextUnits, nextPlanArea, nextFloorPlanUrl) {
            unitsData = nextUnits || [];
            if (nextPlanArea) planArea = nextPlanArea;
            if (nextFloorPlanUrl !== undefined) floorPlanUrl = nextFloorPlanUrl;
            rebuildPlanMesh();
            rebuildUnits();
        },
        focusUnit(id) { selectUnit(id); },
        resetView() { deselect(); },
        getSelected() { return selectedId; },
        dispose() {
            cancelAnimationFrame(raf);
            ro.disconnect();
            renderer.domElement.removeEventListener('pointermove', onPointerMove);
            renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
            renderer.domElement.removeEventListener('pointerdown', onPointerDown);
            renderer.domElement.removeEventListener('click', onClick);
            disposeUnits();
            if (planMesh) { scene.remove(planMesh); planMesh.geometry.dispose(); planMat.dispose(); }
            floor.geometry.dispose(); floorMat.dispose();
            gridMajor.geometry.dispose(); gridMajor.material.dispose();
            gridMinor.geometry.dispose(); gridMinor.material.dispose();
            frame.geometry.dispose(); frame.material.dispose();
            renderer.dispose();
            composer.dispose && composer.dispose();
            container.removeChild(renderer.domElement);
            overlay.remove();
        },
    };
}
